import { useCallback, useMemo, type ComponentProps } from "react";
import {
  Link as RouterLink,
  Navigate as RouterNavigate,
  useLocation as useRouterLocation,
  useNavigate as useRouterNavigate,
  type NavigateFunction,
  type NavigateOptions,
  type NavigateProps,
  type To,
} from "react-router";
import { useActiveTeam } from "~/hooks/useTeamAccess";
import { stripTeamPrefix, teamPath } from "~/lib/team-path";

export function useTeamPath() {
  const activeTeam = useActiveTeam();
  const teamId = activeTeam?.id;
  const domain = activeTeam?.domain ?? "";
  return useCallback(
    (path: string) =>
      teamPath(teamId === undefined ? undefined : { id: teamId, domain }, path),
    [teamId, domain]
  );
}

function useTeamTo() {
  const toTeamPath = useTeamPath();
  return useCallback(
    (to: To): To => {
      if (typeof to === "string") return toTeamPath(to);
      return to.pathname ? { ...to, pathname: toTeamPath(to.pathname) } : to;
    },
    [toTeamPath]
  );
}

export function Link({ to, ...props }: ComponentProps<typeof RouterLink>) {
  const toTeamTo = useTeamTo();
  return <RouterLink to={toTeamTo(to)} {...props} />;
}

export function Navigate({ to, ...props }: NavigateProps) {
  const toTeamTo = useTeamTo();
  return <RouterNavigate to={toTeamTo(to)} {...props} />;
}

export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  const toTeamTo = useTeamTo();
  return useCallback(
    (to: To | number, options?: NavigateOptions) =>
      typeof to === "number" ? navigate(to) : navigate(toTeamTo(to), options),
    [navigate, toTeamTo]
  ) as NavigateFunction;
}

export function useLocation() {
  const location = useRouterLocation();
  return useMemo(
    () => ({ ...location, pathname: stripTeamPrefix(location.pathname) }),
    [location]
  );
}
